// src/pages/Reports/Reports.jsx
// -----------------------------------------------------------------------------
// Reports screen for the internal team (Requirement #23: reports for
// long-pending tickets, Additional Requirement 7: standard reports + Excel
// export). Everything comes from /api/Report and only covers tickets the user
// may see.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import dashboardService from '../../api/dashboardService';
import projectService from '../../api/projectService';
import Loader from '../../components/common/Loader';

const EXPORTS = [
  { type: 'summary', label: 'Summary report' },
  { type: 'long-pending', label: 'Long-pending tickets' },
  { type: 'tickets', label: 'All tickets (filtered)' },
];

function CountTable({ title, label, items }) {
  return (
    <div className="card">
      <div className="sidebar-panel-title">{title}</div>
      {items.length === 0 ? (
        <p className="text-muted">No data.</p>
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>{label}</th>
              <th style={{ textAlign: 'right' }}>Tickets</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.name}>
                <td>{i.name}</td>
                <td style={{ textAlign: 'right' }}>{i.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Reports() {
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ projectId: '', from: '', to: '', longPendingDays: 7 });
  const [summary, setSummary] = useState(null);
  const [longPending, setLongPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  const queryParams = () =>
    Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined));

  const load = async () => {
    setLoading(true);
    try {
      const params = queryParams();
      const [summaryRes, pendingRes] = await Promise.all([
        dashboardService.getSummary(params),
        dashboardService.getLongPendingTickets({ ...params, days: filters.longPendingDays }),
      ]);
      setSummary(summaryRes.data);
      setLongPending(pendingRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load reports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    projectService.getAll().then((res) => setProjects(res.data || [])).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async (reportType) => {
    setExporting(reportType);
    try {
      const res = await dashboardService.exportReport(reportType, queryParams());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AFix_${reportType}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export the report.');
    } finally {
      setExporting('');
    }
  };

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <div className="subtitle">Ticket status, workload, project time and long-pending tickets.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {EXPORTS.map((x) => (
            <button key={x.type} className="btn btn-outline" onClick={() => handleExport(x.type)} disabled={!!exporting}>
              {exporting === x.type ? 'Exporting...' : `⬇ ${x.label}`}
            </button>
          ))}
        </div>
      </div>

      <form
        className="table-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}
      >
        <select value={filters.projectId} onChange={setFilter('projectId')}>
          <option value="">All my projects</option>
          {projects.map((p) => (
            <option key={p.projectID} value={p.projectID}>
              {p.projectCode} — {p.projectName}
            </option>
          ))}
        </select>
        <label style={{ fontSize: 13 }}>
          Created from <input type="date" value={filters.from} onChange={setFilter('from')} />
        </label>
        <label style={{ fontSize: 13 }}>
          to <input type="date" value={filters.to} onChange={setFilter('to')} />
        </label>
        <label style={{ fontSize: 13 }}>
          Long-pending after{' '}
          <input
            type="number"
            min={1}
            value={filters.longPendingDays}
            onChange={setFilter('longPendingDays')}
            style={{ width: 64 }}
          />{' '}
          days without update
        </label>
        <button type="submit" className="btn btn-primary">
          Apply
        </button>
      </form>

      {loading || !summary ? (
        <Loader label="Loading reports..." />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              ['Total', summary.total],
              ['Open', summary.open],
              ['Closed', summary.closed],
              ['Client-raised', summary.clientRaised],
              ['Internally raised', summary.internallyRaised],
              [`Long pending (${summary.longPendingDays}+ days)`, summary.longPending],
            ].map(([label, value]) => (
              <div key={label} className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 26, fontWeight: 800 }}>{value}</div>
                <div className="text-muted" style={{ fontSize: 13 }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 20 }}>
            <CountTable title="By Status" label="Status" items={summary.byStatus || []} />
            <CountTable title="By Severity" label="Severity" items={summary.bySeverity || []} />
            <CountTable title="By Ticket Type" label="Type" items={summary.byType || []} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, marginBottom: 20 }}>
            <div className="card" style={{ overflowX: 'auto' }}>
              <div className="sidebar-panel-title">Time by Project</div>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Open</th>
                    <th>Closed</th>
                    <th title="Average hours from creation to resolution">Avg resolution (h)</th>
                    <th title="Sum of the age of all open tickets">Open ticket-days</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.byProject || []).map((p) => (
                    <tr key={p.projectId}>
                      <td>{p.projectName}</td>
                      <td>{p.open}</td>
                      <td>{p.closed}</td>
                      <td>{p.avgResolutionHours}</td>
                      <td>{p.totalOpenDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card" style={{ overflowX: 'auto' }}>
              <div className="sidebar-panel-title">Team Workload (current assignments)</div>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Team member</th>
                    <th>Open</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.workload || []).map((w) => (
                    <tr key={w.userId}>
                      <td>{w.userName}</td>
                      <td>{w.openAssigned}</td>
                      <td>{w.totalAssigned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ overflowX: 'auto' }}>
            <div className="sidebar-panel-title">
              Long-pending tickets (open, no update for {summary.longPendingDays}+ days)
            </div>
            {longPending.length === 0 ? (
              <p className="text-muted">Nothing is long-pending. 🎉</p>
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Title</th>
                    <th>Project</th>
                    <th>Status</th>
                    <th>Severity</th>
                    <th>Assignees</th>
                    <th>Days since update</th>
                  </tr>
                </thead>
                <tbody>
                  {longPending.map((t) => (
                    <tr key={t.ticketId}>
                      <td>
                        <Link to={`/tickets/${t.ticketId}`}>{t.ticketNumber}</Link>
                      </td>
                      <td>{t.title}</td>
                      <td>{t.projectName}</td>
                      <td>{t.statusName}</td>
                      <td>{t.severityName}</td>
                      <td>{t.assignees || '—'}</td>
                      <td>{t.daysSinceUpdate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
